/**
 * Erreur BLOQUANTE — composant partagé unique (décision 3.8 de Valérie, 25/09/2026).
 *
 * Un écran qui ne peut pas se charger le dit (jamais un écran vide ni un chargement infini) :
 * le message, « Réessayer » et, après 3 échecs d'affilée, « Prévenir le support ». Le clic envoie
 * l'erreur à Sentry depuis le navigateur (pour obtenir un identifiant), puis appelle
 * `POST /api/support/report` : email automatique à tous les ADM (qui, où, quoi, quand,
 * environnement, identifiant Sentry). Aucun contenu saisi n'est transmis.
 *
 * Réservé aux erreurs qui BLOQUENT l'utilisateur : pour une action ratée (non bloquante), un simple
 * message d'erreur suffit, sans « Prévenir le support ».
 *
 * Réutilisé par « Mes sessions » et par les écrans bloquants de Pilot et du Plan — ne pas en
 * recopier une variante : passer par ce composant.
 */
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Loader2, LifeBuoy, RotateCcw } from "lucide-react";
import * as Sentry from "@sentry/browser";
import { Button } from "./ui/button";
import { supportApi } from "../lib/api";
import type { SupportAction, SupportScreen } from "../lib/support-screens";

/** Nombre d'échecs à partir duquel « Prévenir le support » apparaît. */
export const SUPPORT_AFTER_FAILURES = 3;

/** Compteur d'échecs CONSÉCUTIFS d'un chargement : `fail()` dans le catch, `reset()` au succès. */
export function useFailureCount() {
  const [failures, setFailures] = useState(0);
  const fail = useCallback(() => setFailures((n) => n + 1), []);
  const reset = useCallback(() => setFailures(0), []);
  return { failures, fail, reset };
}

/** Code technique lisible d'une erreur (jamais de contenu saisi : le nom et le message de l'erreur). */
export function technicalCode(error: unknown): string {
  if (error instanceof Error) return `${error.name}: ${error.message}`.slice(0, 300);
  if (typeof error === "string") return error.slice(0, 300);
  return "";
}

export function BlockingError({ screen, action = "load", message, error, failures, onRetry, compact = false }: {
  /** Identifiant stable de l'écran — liste fermée partagée avec le serveur (`support-screens.ts`). */
  screen: SupportScreen;
  /** Ce que l'écran essayait de faire (« load » par défaut, « reload »). */
  action?: SupportAction;
  /** Message affiché, déjà traduit. */
  message: string;
  error?: unknown;
  /** Échecs consécutifs (`useFailureCount`). */
  failures: number;
  onRetry: () => void | Promise<unknown>;
  /** Variante resserrée pour un panneau ou une fenêtre. */
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const [retrying, setRetrying] = useState(false);
  const [sending, setSending] = useState(false);
  const [reported, setReported] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  const retry = async () => {
    if (retrying) return;
    setRetrying(true);
    try { await onRetry(); } finally { setRetrying(false); }
  };

  const report = async () => {
    if (sending || reported) return;
    setSending(true); setSendError(null);
    const code = technicalCode(error);
    const sentryEventIds: string[] = [];
    try {
      // Envoi explicite à Sentry pour obtenir un identifiant à joindre au signalement.
      if (Sentry.getClient()) {
        const id = Sentry.captureException(error instanceof Error ? error : new Error(code || `[${screen}] ${action}`), {
          tags: { support_report: "true", screen, action },
        });
        if (id) sentryEventIds.push(id);
      }
    } catch { /* Sentry absent : le signalement part quand même */ }
    try {
      await supportApi.report({ screen, action, message, code, path: typeof window !== "undefined" ? window.location.pathname : "", sentryEventIds });
      setReported(true);
    } catch (e) {
      // 429 : trop de signalements aujourd'hui (au plus 3 par 24 h) ; sinon, envoi impossible.
      setSendError(t((e as { status?: number } | null)?.status === 429 ? "blockingError.tooMany" : "blockingError.sendFailed"));
    } finally {
      setSending(false);
    }
  };

  const showSupport = failures >= SUPPORT_AFTER_FAILURES;
  return (
    <div role="alert" data-blocking-error={screen}
      className={`rounded-xl border ${compact ? "px-3 py-2.5" : "px-4 py-3"}`}
      style={{ background: "#FDF1F0", borderColor: "#F49390" }}>
      <p className="flex items-start gap-2 text-sm font-semibold" style={{ color: "#C0423F" }}>
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{message}</span>
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <Button type="button" size="sm" variant="outline" onClick={() => void retry()} disabled={retrying}>
          {retrying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
          {t("blockingError.retry")}
        </Button>
        {showSupport && (
          <Button type="button" size="sm" onClick={() => void report()} disabled={sending || reported}>
            {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LifeBuoy className="h-3.5 w-3.5" />}
            {t("blockingError.notifySupport")}
          </Button>
        )}
      </div>
      {reported && <p className="mt-2 text-xs font-medium" style={{ color: "#1E7A3C" }} role="status">{t("blockingError.sent")}</p>}
      {sendError && <p className="mt-2 text-xs" style={{ color: "#C0423F" }}>{sendError}</p>}
    </div>
  );
}
